import { useRef } from "react";
import {
  cubicBezier,
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { EASE_EDITORIAL } from "@/shared/components/motion/motion";

/** Per-step starting offset — a deliberate "tossed on the table" scatter that
 *  settles to zero when the row gathers. */
type Scatter = { x: number; y: number; rotate: number };

const STEPS: { n: string; title: string; body: string; scatter: Scatter }[] = [
  {
    n: "01",
    title: "Gather",
    body: "Bring in your existing list or share a collect link and let guests add themselves.",
    scatter: { x: -64, y: 40, rotate: -10 },
  },
  {
    n: "02",
    title: "Review",
    body: "Welcome, merge, or set aside each submission. Nothing joins your list until you say so.",
    scatter: { x: 52, y: -52, rotate: 8 },
  },
  {
    n: "03",
    title: "Organize",
    body: "Group people into households and arrange them into the lists that suit each occasion.",
    scatter: { x: -44, y: -34, rotate: 7 },
  },
  {
    n: "04",
    title: "Export",
    body: "Choose a list, pick a saved format, and hand a clean, print-ready file to any planner or vendor.",
    scatter: { x: 66, y: 38, rotate: -7 },
  },
];

/** Editorial easing applied to each scroll-driven transform so the scatter
 *  settles with the same curve as the rest of the marketing motion. */
const EASE = cubicBezier(...EASE_EDITORIAL);

const CARD_CLASS = "flex flex-col gap-4 rounded-xl glass-surface p-6 shadow-md";

function CardBody({ step }: { step: (typeof STEPS)[number] }) {
  return (
    <>
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full glass-surface-subtle type-label-lg text-brand">
        {step.n}
      </span>
      <h3 className="type-heading-xs text-text">{step.title}</h3>
      <p className="type-body-base text-text-muted">{step.body}</p>
    </>
  );
}

/** One card whose offset, rotation, opacity, and scale are driven by the shared
 *  scroll progress: scattered at progress 0, gathered at 1. */
function ScrollCard({
  step,
  progress,
}: {
  step: (typeof STEPS)[number];
  progress: MotionValue<number>;
}) {
  const { x: sx, y: sy, rotate: sr } = step.scatter;
  const x = useTransform(progress, [0, 1], [sx, 0], { ease: EASE });
  const y = useTransform(progress, [0, 1], [sy, 0], { ease: EASE });
  const rotate = useTransform(progress, [0, 1], [sr, 0], { ease: EASE });
  const opacity = useTransform(progress, [0, 0.5], [0, 1], { ease: EASE });
  const scale = useTransform(progress, [0, 1], [0.92, 1], { ease: EASE });

  return (
    <motion.div className={CARD_CLASS} style={{ x, y, rotate, opacity, scale }}>
      <CardBody step={step} />
    </motion.div>
  );
}

/**
 * How it works. Each step is a glass card. The cards are tied to scroll
 * position: scattered — tossed at varied offsets and angles — while the row is
 * still entering from below, then gathered into a tidy row as it scrolls up to
 * the viewport center, a literal read of the "scattered to sorted" promise.
 * Because the motion is scroll-linked it reverses: scrolling back up re-scatters
 * the cards. Reduced motion renders the row already collected.
 */
export function HowItWorksSection() {
  const reduced = useReducedMotion();
  const gridRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: gridRef,
    offset: ["start end", "center center"],
  });

  return (
    <Section spacing="marketing" id="how-it-works">
      <Container size="5xl">
        <div className="mb-16 flex flex-col items-center gap-4 text-center">
          <p className="type-label-sm tracking-wider uppercase text-text-muted">
            How it works
          </p>
          <h2 className="type-heading-md max-w-2xl text-text">
            From scattered to sorted, in four steps.
          </h2>
        </div>

        {reduced ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step) => (
              <div key={step.n} className={CARD_CLASS}>
                <CardBody step={step} />
              </div>
            ))}
          </div>
        ) : (
          <div ref={gridRef} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step) => (
              <ScrollCard key={step.n} step={step} progress={scrollYProgress} />
            ))}
          </div>
        )}
      </Container>
    </Section>
  );
}
